import { useEffect, createContext, useContext, useMemo, useRef, useState, ReactNode } from "react";
import { useStoreActions, useStoreState, Session } from "ezStore";
import { useLazyQuery } from "@apollo/client";

import { getLanguageCode, isRTR } from "utils";

import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { CircularProgress, Paper } from "@mui/material";

import { convertSettingsToData } from "view/admin/utils/AdminUtils";

import { PluginFields } from "types/enums";

import { StatusType } from "__generated__/enums";
import { GetLastGasoilPriceDocument, GetLastGasoilPriceQuery } from "__generated__/view/gasoilprices/graphql/queries.generated";
import { GetAllStatusesDocument, GetAllStatusesQuery } from "__generated__/view/statuses/graphql/queries.generated";
import { GetSettingsDocument, LanguageSetByCodeDocument } from "__generated__/view/admin/graphql/admin.queries.generated";
import { GetDefaultUserFrontendTableSettingsDocument } from "__generated__/view/defaultTableSettings/graphql/defaultTableSettings.queries.generated";
import { StatusFragmentFragment } from "__generated__/view/statuses/graphql/fragments.generated";
import { GetSettingsCurrenciesDocument } from "__generated__/graphql/queries.generated";

const defaultSettings = {
	settings: {
		timestamp: null,
		applicationVersion: null,
		plugins: {},
		settings: {},
		fields: {} as Record<PluginFields, string[]>,
		multiValues: {},
	},
	language: null,
	currencies: [],
	statuses: {
		CARGO: [],
		CALL: [],
		VOYAGE: [],
		CHARTER: [],
		DECLARATION: [],
		INVOICE: [],
	} as Record<StatusType, StatusFragmentFragment[]>,
	gasoilprice: null,
};

// eslint-disable-next-line react-refresh/only-export-components
export const ApplicationSettingsContext = createContext<Partial<Session & { session?: Session }>>(defaultSettings);

export const ApplicationSettingsProvider = ({ children }: { children: ReactNode }) => {
	const session = useStoreState((store) => store.session);
	const { setAppSettings } = useStoreActions((store) => store.session);
	const { instance: { isAuthenticated } } = useCoastguard();

	const [ isInitializing, setIsInitializing ] = useState(true);
	const hasFetchedRef = useRef(false);

	const [ getAllStatuses ] = useLazyQuery<GetAllStatusesQuery>(GetAllStatusesDocument);
	const [ getLanguageSetByCode ] = useLazyQuery(LanguageSetByCodeDocument);
	const [ getApplicationSettings ] = useLazyQuery(GetSettingsDocument);
	const [ getCurrencies ] = useLazyQuery(GetSettingsCurrenciesDocument);
	const [ getLastGasoilPrice ] = useLazyQuery<GetLastGasoilPriceQuery>(GetLastGasoilPriceDocument);
	const [ getDefaultTableSettings ] = useLazyQuery(GetDefaultUserFrontendTableSettingsDocument);

	useEffect(() => {
		if (!isAuthenticated) {
			setIsInitializing(false);
			return;
		}

		if (hasFetchedRef.current) {
			return;
		}

		const previousLocale = localStorage.getItem("bosLanguageLocale");
		const currentLanguageCode = getLanguageCode(session.user?.language);

		if (session.settings && previousLocale === currentLanguageCode) {
			setIsInitializing(false);
			return;
		}

		hasFetchedRef.current = true;
		setIsInitializing(true);

		const includeGasoilPrices = isRTR(session) && Boolean(session.publicSettings?.plugins?.gasoilPrice);

		Promise.all([
			getApplicationSettings().catch(() => ({ data: null })),
			getCurrencies().catch(() => ({ data: null })),
			getAllStatuses().catch(() => ({ data: null })),
			getLanguageSetByCode({ variables: { code: currentLanguageCode } }).catch(() => ({ data: null })),
			getDefaultTableSettings().catch(() => ({ data: null })),
			includeGasoilPrices ? getLastGasoilPrice().catch(() => ({ data: null })) : Promise.resolve(null),
		])
			.then(([ settingsRes, currenciesRes, statusesRes, languageRes, tableSettingsRes, gasoilRes ]) => {
				const settings = settingsRes?.data?.settings ? convertSettingsToData(settingsRes.data.settings) : undefined;

				const currencies = currenciesRes?.data?.settingsCurrencies?.map((currency) => ({
					isoCode: currency.isoCode ?? undefined,
					symbol: currency.symbol ?? undefined,
					rate: currency.rate ?? undefined,
				})) ?? [];

				const initialStatuses: Record<StatusType, StatusFragmentFragment[]> = {
					CARGO: [], CALL: [], VOYAGE: [], CHARTER: [], DECLARATION: [], INVOICE: [],
				};

				const statuses = statusesRes?.data?.allStatuses?.reduce((acc, cur) => {
					if (cur.type && acc[cur.type]) {
						acc[cur.type].push(cur);
					}
					return acc;
				}, initialStatuses) ?? initialStatuses;

				const languageSet = languageRes?.data?.languageSetByCode ?? [];

				const values = languageSet.reduce<Record<string, string>>((acc, cur) => {
					if (cur?.key) {
						acc[cur.key] = cur.translation ?? "";
					}
					return acc;
				}, {});

				localStorage.setItem("bosLanguageKeys", JSON.stringify(values));
				localStorage.setItem("bosLanguageLocale", currentLanguageCode);

				const language = { language: currentLanguageCode, values };
				const tableSettings = tableSettingsRes?.data?.defaultUserFrontendTableSettings ?? null;
				const gasoilprice = gasoilRes?.data?.lastGasoilPrice ?? null;

				setAppSettings({
					settings,
					currencies,
					statuses,
					language,
					tableSettings,
					gasoilprice,
				});

				if (previousLocale && previousLocale !== currentLanguageCode) {
					window.location.reload();
				}
			})
			.catch((err) => {
				console.error("Failed to fetch application settings:", err);
			})
			.finally(() => {
				setIsInitializing(false);
			});
	}, [ isAuthenticated, session.user?.language ]);

	const contextValue = useMemo(() => ({
		...session,
		session,
	}), [ session ]);

	if (isInitializing) {
		return (
			<Paper sx={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
				<CircularProgress size={30} />
			</Paper>
		);
	}

	return (
		<ApplicationSettingsContext.Provider value={contextValue}>
			{children}
		</ApplicationSettingsContext.Provider>
	);
};

// eslint-disable-next-line react-refresh/only-export-components
export function useSettings() {
	return useContext(ApplicationSettingsContext);
}
